import { type RefObject, useLayoutEffect } from "react";

export function useScrollFade({
  scrollRef,
  maxFade = 16,
}: {
  scrollRef: RefObject<HTMLElement | null>;
  maxFade?: number;
}) {
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let raf = 0;
    const apply = () => {
      raf = 0;
      el.style.setProperty(
        "--fade-top",
        `${Math.min(el.scrollTop, maxFade)}px`
      );
      el.style.setProperty(
        "--fade-bottom",
        `${Math.min(el.scrollHeight - el.clientHeight - el.scrollTop, maxFade)}px`
      );
      el.style.setProperty(
        "--fade-left",
        `${Math.min(el.scrollLeft, maxFade)}px`
      );
      el.style.setProperty(
        "--fade-right",
        `${Math.min(el.scrollWidth - el.clientWidth - el.scrollLeft, maxFade)}px`
      );
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };

    apply();
    el.addEventListener("scroll", schedule, { passive: true });
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener("scroll", schedule);
      resizeObserver.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef, maxFade]);
}
